import { merge, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ObservableUnifiedPlayer } from '../../types/ObservableUnifiedPlayer';
import { UseTrackingMappedMedia } from '../../plugins/Tracking';
import {
  AD_BEGIN,
  AD_BREAKBEGIN,
  AD_BREAKEND,
  AD_END,
  AD_PAUSE,
  AD_TIMEUPDATE,
  MEDIA_ENDED,
  MEDIA_PAUSE,
  MEDIA_PLAY,
  MEDIA_SEEKING,
  MEDIA_TIMEUPDATE,
  MEDIA_WAITING,
  PLAYER_DESTROY,
} from '../../core/events';
import { PlayUnifiedLiveTrackingQuery } from '../dataProviders/__generated__/Tracking';
import {
  type UnifiedTrackingMachineEvent,
  UnifiedTrackingMachineEventTypes,
  UnifiedTrackingMachine,
} from './UnifiedTrackingMachine';

export class UnifiedTrackingPlugin {
  #subscriptionToCleanOnDestroy: Subscription[] = [];
  machineFn: () => UnifiedTrackingMachine;

  constructor(machineFn: () => UnifiedTrackingMachine) {
    this.machineFn = machineFn;
  }

  track(
    player: ObservableUnifiedPlayer,
    media$: Observable<UseTrackingMappedMedia>,
    tracking$: Observable<PlayUnifiedLiveTrackingQuery>
  ) {
    // We create our state machine
    let machine: UnifiedTrackingMachine | null = null;

    // We subscribe to the player to get the data that the machine needs
    const sub = merge(
      media$.pipe(
        map(media => ({
          type: UnifiedTrackingMachineEventTypes.Load,
          value: {
            media,
            currentTime: player.currentTime,
            subtitlesAvailable: player.subtitlesAvailable,
            subtitlesEnabled: player.subtitlesEnabled,
          },
        }))
      ),
      player
        .observe(AD_BREAKBEGIN)
        .pipe(map(value => ({ type: UnifiedTrackingMachineEventTypes.AdBreakBegin, value }))),
      player
        .observe(AD_BREAKEND)
        .pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.AdBreakEnd }))),
      player
        .observe(AD_BEGIN)
        .pipe(map(value => ({ type: UnifiedTrackingMachineEventTypes.AdBegin, value }))),
      player.observe(AD_END).pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.AdEnd }))),
      player.observe(MEDIA_PLAY).pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.Play }))),
      player
        .observe(MEDIA_SEEKING)
        .pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.Seeking }))),
      player
        .observe(MEDIA_PAUSE)
        .pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.Pause }))),
      player.observe(AD_PAUSE).pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.Pause }))),
      player
        .observe(MEDIA_ENDED)
        .pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.Stop }))),
      player.observe(MEDIA_TIMEUPDATE).pipe(
        map(value => ({
          type: UnifiedTrackingMachineEventTypes.TimeUpdate,
          value: {
            type: value.type,
            currentTime: value.currentTime ?? value.currentMediaTime ?? 0,
            droppedFrames: value.droppedFrames,
            currentBitrate: player.currentBitrate,
          },
        }))
      ),
      player
        .observe(MEDIA_WAITING)
        .pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.Buffering }))),
      player.observe(AD_TIMEUPDATE).pipe(
        map(value => ({
          type: UnifiedTrackingMachineEventTypes.TimeUpdate,
          value: {
            type: value.type,
            currentTime: value.currentTime ?? value.currentMediaTime,
          },
        }))
      ),
      player
        .observe(PLAYER_DESTROY)
        .pipe(map(() => ({ type: UnifiedTrackingMachineEventTypes.Destroy }))),
      tracking$.pipe(
        map(value => ({
          type: UnifiedTrackingMachineEventTypes.NewTrackingEntries,
          value,
        }))
      )
    ).subscribe({
      next: event => {
        if (machine === null) {
          machine = this.machineFn();
        }

        // We send the event to the machine
        machine.send(event as UnifiedTrackingMachineEvent);

        if (event.type === UnifiedTrackingMachineEventTypes.Destroy) {
          // We don't want to use that machine anymore, create a new one on the next event
          machine = null;
        }
      },
      // eslint-disable-next-line no-console
      error: console.error,
    });

    this.#subscriptionToCleanOnDestroy.push(sub);
  }

  destroy() {
    this.#subscriptionToCleanOnDestroy.forEach(s => s.unsubscribe());
    this.#subscriptionToCleanOnDestroy = [];
  }
}
